package com.schoolconnect.app.feature.common

/** Single pagination policy for all server-backed list screens. */
object PaginationPolicy {
    const val DEFAULT_PAGE_SIZE: Int = 20
    const val MIN_PAGE_SIZE: Int = 1
    val PAGE_SIZE_OPTIONS: List<Int> = listOf(10, 20, 50)
}

data class PageRequest(
    val pageIndex: Int,
    val pageSize: Int
) {
    val safePageIndex: Int get() = pageIndex.coerceAtLeast(0)
    val safePageSize: Int get() = pageSize.coerceAtLeast(PaginationPolicy.MIN_PAGE_SIZE)
    val offset: Int get() = safePageIndex * safePageSize
}

data class PageWindow(
    val pageIndex: Int,
    val pageSize: Int,
    val totalCount: Int,
    val startIndex: Int,
    val endIndex: Int,
    val pageCount: Int
)

fun pageCountForTotal(totalCount: Int, pageSize: Int): Int {
    val safeTotal = totalCount.coerceAtLeast(0)
    val safePageSize = pageSize.coerceAtLeast(PaginationPolicy.MIN_PAGE_SIZE)
    return ((safeTotal + safePageSize - 1) / safePageSize).coerceAtLeast(1)
}

fun pageWindow(pageIndex: Int, pageSize: Int, totalCount: Int): PageWindow {
    val request = PageRequest(pageIndex, pageSize)
    val safeTotal = totalCount.coerceAtLeast(0)
    val count = pageCountForTotal(safeTotal, request.safePageSize)
    val safeIndex = request.safePageIndex.coerceAtMost(count - 1)
    val start = if (safeTotal == 0) 0 else safeIndex * request.safePageSize + 1
    val end = if (safeTotal == 0) 0 else minOf((safeIndex + 1) * request.safePageSize, safeTotal)
    return PageWindow(
        pageIndex = safeIndex,
        pageSize = request.safePageSize,
        totalCount = safeTotal,
        startIndex = start,
        endIndex = end,
        pageCount = count
    )
}

fun pageFooterText(window: PageWindow, itemLabel: String): String {
    val label = if (window.totalCount == 1 && itemLabel.endsWith("s")) itemLabel.dropLast(1) else itemLabel
    return "Showing ${window.startIndex} to ${window.endIndex} of ${window.totalCount} $label"
}
